import { Loader2, RefreshCw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "./ui/button.js";
import { Progress } from "./ui/progress.js";
import { apiErrorMessage } from "../lib/api.js";
import {
  PLATFORM_LABEL,
  SYNC_NOW_CONFIRM_TEXT,
  syncBlockedReason,
  syncStatusText,
  type PlatformSyncStatus,
} from "../lib/integrations.js";

/**
 * SYNC NOW for one platform: its sync state, the button, and the confirmation the ticket asks for —
 * nothing starts until the merchant confirms. Enter confirms, Escape or a click outside cancels.
 *
 * The button is never disabled without saying why (owner/admin only, already running, cooldown,
 * no account selected): a dead button with no reason reads as broken.
 */
export function SyncNowPanel({
  status,
  onStart,
  now = new Date(),
}: {
  readonly status: PlatformSyncStatus;
  readonly onStart: () => Promise<void>;
  readonly now?: Date;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = PLATFORM_LABEL[status.platform];
  const running = status.state === "running";
  const reason = syncBlockedReason(status, now);

  const start = async (): Promise<void> => {
    setConfirming(false);
    setStarting(true);
    setError(null);
    try {
      await onStart();
    } catch (caught) {
      setError(apiErrorMessage(caught, `Could not start the ${label} sync.`));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3" data-testid={`sync-now-${status.platform}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{label} data sync</p>
          <p className="text-muted-foreground text-xs" data-testid="sync-status">
            {syncStatusText(status, now)}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={!status.canSync || starting}
          onClick={() => setConfirming(true)}
          title={reason ?? undefined}
        >
          {starting || status.state === "queued" || running ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {running || status.state === "queued" ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      {running && (
        <div className="flex flex-col gap-1">
          <Progress value={status.progress} />
          {status.message !== null && <p className="text-muted-foreground text-xs">{status.message}</p>}
        </div>
      )}
      {reason !== null && !running && status.state !== "queued" && (
        <p className="text-muted-foreground text-xs" data-testid="sync-blocked">
          {reason}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      )}

      {confirming && <SyncNowConfirm label={label} onCancel={() => setConfirming(false)} onConfirm={() => void start()} />}
    </div>
  );
}

function SyncNowConfirm({
  label,
  onCancel,
  onConfirm,
}: {
  readonly label: string;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  const confirmButton = useRef<HTMLButtonElement>(null);
  // Focus on Continue, so Enter continues — the ticket's "Enter to continue".
  useEffect(() => confirmButton.current?.focus(), []);

  return (
    <div
      className="modal-backdrop-animate fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onClick={onCancel}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
        if (event.key === "Enter") {
          event.preventDefault();
          onConfirm();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sync-now-title"
        className="modal-content-animate bg-card flex w-full max-w-[460px] flex-col gap-4 rounded-2xl border p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 id="sync-now-title" className="text-foreground text-base font-bold">
            Sync {label} now?
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg p-1.5 transition-colors"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="text-foreground text-sm leading-relaxed" data-testid="sync-confirm-text">
          {SYNC_NOW_CONFIRM_TEXT}
        </p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" ref={confirmButton} onClick={onConfirm}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
